import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Check, X, HelpCircle, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const REQUEST_TYPES = [
  { value: "available", label: "◎ 入れます", description: "この日はシフトに入って大丈夫", color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: Check },
  { value: "either", label: "○ 必要なら入れます", description: "人が少ない時ならOK (お願い候補)", color: "bg-sky-100 text-sky-700 border-sky-300", icon: HelpCircle },
  { value: "unavailable", label: "× 入れません", description: "この日は出勤不可", color: "bg-rose-100 text-rose-700 border-rose-300", icon: X },
];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function pad(n) { return String(n).padStart(2, "0"); }

export default function ShiftRequest() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [existing, setExisting] = useState({});
  const [drawerDate, setDrawerDate] = useState(null);
  const [form, setForm] = useState({ request_type: "available", start_time: "", end_time: "", memo: "", time_mode: "default" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkWeekday, setBulkWeekday] = useState("1");
  const [bulkType, setBulkType] = useState("available");
  const [bulkTimeMode, setBulkTimeMode] = useState("default"); // default | anytime | custom
  const [bulkStart, setBulkStart] = useState("");
  const [bulkEnd, setBulkEnd] = useState("");

  const period = periods.find((p) => p.id === selectedPeriodId);
  const days = period ? getDaysInMonth(period.year, period.month) : 0;
  const weeks = useMemo(() => {
    if (!period) return [];
    const first = new Date(period.year, period.month - 1, 1).getDay();
    const total = days;
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const w = [];
    for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
    return w;
  }, [period, days]);

  useEffect(() => {
    api.get("/shift-periods").then((r) => {
      setPeriods(r.data);
      const accepting = r.data.find((p) => p.status === "accepting");
      if (accepting) setSelectedPeriodId(accepting.id);
      else if (r.data.length > 0) setSelectedPeriodId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedPeriodId) return;
    api.get(`/shift-requests?shift_period_id=${selectedPeriodId}`).then((r) => {
      const map = {};
      for (const req of r.data) map[req.date] = req;
      setExisting(map);
    });
  }, [selectedPeriodId]);

  const hasDefault = user?.default_start_time && user?.default_end_time;
  const hasAnytimeDefault = !!user?.default_anytime_ok;

  const openDay = (day) => {
    if (!day || !period) return;
    const dateStr = `${period.year}-${pad(period.month)}-${pad(day)}`;
    const e = existing[dateStr];
    let timeMode = "default";
    if (e) {
      if (e.request_type === "available") {
        if (!e.start_time && !e.end_time) timeMode = "anytime";
        else if (
          hasDefault &&
          e.start_time === user.default_start_time &&
          e.end_time === user.default_end_time
        ) timeMode = "default";
        else timeMode = "custom";
      }
    } else if (hasAnytimeDefault) {
      timeMode = "anytime";
    } else if (!hasDefault) {
      timeMode = "anytime";
    }
    setForm({
      request_type: e?.request_type || "available",
      start_time: e?.start_time || "",
      end_time: e?.end_time || "",
      memo: e?.memo || "",
      time_mode: timeMode,
    });
    setDrawerDate(dateStr);
  };

  const save = async () => {
    let start = "", end = "";
    if (form.request_type === "available") {
      if (form.time_mode === "default" && hasDefault) {
        start = user.default_start_time;
        end = user.default_end_time;
      } else if (form.time_mode === "custom") {
        start = form.start_time;
        end = form.end_time;
      }
      // anytime → both empty
    }
    try {
      const { data } = await api.post("/shift-requests", {
        shift_period_id: selectedPeriodId,
        date: drawerDate,
        request_type: form.request_type,
        start_time: start || null,
        end_time: end || null,
        memo: form.memo || null,
      });
      setExisting((e) => ({ ...e, [drawerDate]: data }));
      toast.success("希望を保存しました");
      setDrawerDate(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const applyBulk = async () => {
    if (!period) return;
    let start = null, end = null;
    if (bulkType === "available") {
      if (bulkTimeMode === "default" && hasDefault) {
        start = user.default_start_time; end = user.default_end_time;
      } else if (bulkTimeMode === "custom") {
        start = bulkStart || null; end = bulkEnd || null;
      }
    }
    const items = [];
    const wd = parseInt(bulkWeekday, 10);
    for (let d = 1; d <= days; d++) {
      const dt = new Date(period.year, period.month - 1, d);
      if (dt.getDay() === wd) {
        items.push({
          date: `${period.year}-${pad(period.month)}-${pad(d)}`,
          request_type: bulkType,
          start_time: start,
          end_time: end,
        });
      }
    }
    try {
      const { data } = await api.post("/shift-requests/bulk", {
        shift_period_id: selectedPeriodId,
        items,
      });
      const map = { ...existing };
      for (const r of data) map[r.date] = r;
      setExisting(map);
      toast.success(`${items.length}日分を一括設定しました`);
      setBulkOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const readOnly = period && period.status !== "accepting";
  const isLate = period && period.status === "accepting" && period.submission_deadline
    && new Date(period.submission_deadline + "T23:59:59") < new Date();
  const previewTime = () => {
    if (form.request_type !== "available") return "";
    if (form.time_mode === "default" && hasDefault) {
      return `${user.default_start_time}〜${user.default_end_time}`;
    }
    if (form.time_mode === "anytime") return "何時でもOK";
    if (form.start_time && form.end_time) return `${form.start_time}〜${form.end_time}`;
    return "";
  };

  return (
    <div className="p-4 space-y-4 max-w-md mx-auto" data-testid="shift-request-page">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">希望シフト提出</h1>

      <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
        <SelectTrigger data-testid="period-select" className="bg-white">
          <SelectValue placeholder="対象月を選択" />
        </SelectTrigger>
        <SelectContent>
          {periods.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`period-option-${p.year}-${p.month}`}>
              {p.year}年{p.month}月分 ({p.status === "accepting" ? "受付中" : p.status})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {readOnly && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
          希望受付期間外です。変更したい場合は担当者にメッセージしてください。
        </div>
      )}

      {isLate && !readOnly && (
        <div
          data-testid="late-submission-banner"
          className="bg-orange-50 border border-orange-300 rounded-md p-3 text-sm text-orange-900 leading-relaxed"
        >
          <p className="font-bold mb-0.5">⚠ 締切（{period.submission_deadline}）を過ぎていますが、提出は受け付けます</p>
          <p className="text-xs text-orange-700">
            遅れ提出として記録されます。シフトが既に作成中の場合は配置できない可能性があるため、早めに担当者へご相談ください。
          </p>
        </div>
      )}

      {!hasDefault && !hasAnytimeDefault && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
          <span className="font-semibold">ヒント:</span> 「設定」からデフォルト時間を登録すると、希望提出がもっと早くなります。
        </div>
      )}

      {period && (
        <Card className="p-3 bg-white">
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-500 mb-1">
            {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => (
              <div key={i} className={i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : ""}>
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weeks.flat().map((d, i) => {
              if (!d) return <div key={i} />;
              const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
              const e = existing[dateStr];
              const type = REQUEST_TYPES.find((t) => t.value === e?.request_type);
              return (
                <button
                  key={i}
                  data-testid={`day-cell-${d}`}
                  disabled={readOnly}
                  onClick={() => openDay(d)}
                  className={`aspect-square rounded-md text-sm flex flex-col items-center justify-center border ${
                    type ? type.color : "bg-white border-slate-200 text-slate-700"
                  } ${readOnly ? "opacity-60" : "hover:border-blue-500"}`}
                >
                  <span className="font-semibold">{d}</span>
                  {type && <span className="text-[10px] leading-none mt-0.5">{type.label}</span>}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <Button
        variant="outline"
        data-testid="bulk-open-button"
        onClick={() => setBulkOpen(true)}
        disabled={readOnly}
        className="w-full h-12"
      >
        曜日で一括設定
      </Button>

      {/* Day editor drawer */}
      <Drawer open={!!drawerDate} onOpenChange={(o) => !o && setDrawerDate(null)}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{drawerDate}</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 space-y-4">
            <div>
              <Label className="text-xs text-slate-500">区分</Label>
              <div className="grid grid-cols-1 gap-2 mt-1">
                {REQUEST_TYPES.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      data-testid={`request-type-${t.value}`}
                      onClick={() => setForm({ ...form, request_type: t.value })}
                      className={`flex flex-col items-start gap-0.5 px-4 py-3 rounded-md border-2 text-left ${
                        form.request_type === t.value
                          ? `${t.color} border-current`
                          : "bg-white border-slate-200 text-slate-600"
                      }`}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold">
                        <Icon className="w-4 h-4" />
                        {t.label}
                      </span>
                      <span className="text-[11px] opacity-70 pl-6">{t.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {form.request_type === "available" && (
              <div>
                <Label className="text-xs text-slate-500">時間</Label>
                <div className="grid grid-cols-1 gap-2 mt-1">
                  {hasDefault && (
                    <button
                      data-testid="time-mode-default"
                      onClick={() => setForm({ ...form, time_mode: "default" })}
                      className={`flex items-center gap-2 px-4 py-3 rounded-md border-2 text-sm font-semibold ${
                        form.time_mode === "default"
                          ? "bg-blue-50 border-blue-500 text-blue-700"
                          : "bg-white border-slate-200 text-slate-600"
                      }`}
                    >
                      <Sparkles className="w-4 h-4" />
                      デフォルト時間 ({user.default_start_time}〜{user.default_end_time})
                    </button>
                  )}
                  <button
                    data-testid="time-mode-anytime"
                    onClick={() => setForm({ ...form, time_mode: "anytime" })}
                    className={`flex items-center gap-2 px-4 py-3 rounded-md border-2 text-sm font-semibold ${
                      form.time_mode === "anytime"
                        ? "bg-blue-50 border-blue-500 text-blue-700"
                        : "bg-white border-slate-200 text-slate-600"
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    何時でもOK
                  </button>
                  <button
                    data-testid="time-mode-custom"
                    onClick={() => setForm({ ...form, time_mode: "custom" })}
                    className={`flex items-center gap-2 px-4 py-3 rounded-md border-2 text-sm font-semibold ${
                      form.time_mode === "custom"
                        ? "bg-blue-50 border-blue-500 text-blue-700"
                        : "bg-white border-slate-200 text-slate-600"
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    時間を入力する
                  </button>
                  {form.time_mode === "custom" && (
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Input
                        data-testid="start-time-input"
                        type="time"
                        value={form.start_time}
                        onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                      />
                      <Input
                        data-testid="end-time-input"
                        type="time"
                        value={form.end_time}
                        onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                      />
                    </div>
                  )}
                </div>
                {previewTime() && (
                  <p className="text-xs text-slate-500 mt-2">登録する時間: <span className="font-semibold text-slate-900">{previewTime()}</span></p>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">メモ (任意)</Label>
              <Textarea
                data-testid="memo-input"
                value={form.memo || ""}
                onChange={(e) => setForm({ ...form, memo: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DrawerFooter>
            <Button data-testid="save-day-button" onClick={save} className="bg-blue-600 hover:bg-blue-700 h-12 text-base">
              保存
            </Button>
            <Button variant="outline" onClick={() => setDrawerDate(null)}>キャンセル</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Bulk drawer */}
      <Drawer open={bulkOpen} onOpenChange={setBulkOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>曜日で一括設定</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 space-y-3">
            <div>
              <Label className="text-xs">曜日</Label>
              <Select value={bulkWeekday} onValueChange={setBulkWeekday}>
                <SelectTrigger data-testid="bulk-weekday-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => (
                    <SelectItem key={i} value={String(i)}>{w}曜日</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">区分</Label>
              <Select value={bulkType} onValueChange={setBulkType}>
                <SelectTrigger data-testid="bulk-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bulkType === "available" && (
              <div>
                <Label className="text-xs">時間</Label>
                <div className="grid grid-cols-1 gap-2 mt-1">
                  {hasDefault && (
                    <button
                      data-testid="bulk-time-default"
                      onClick={() => setBulkTimeMode("default")}
                      className={`px-3 py-2 rounded-md border-2 text-sm font-semibold text-left ${
                        bulkTimeMode === "default" ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-slate-200 text-slate-600"
                      }`}
                    >
                      デフォルト時間 ({user.default_start_time}〜{user.default_end_time})
                    </button>
                  )}
                  <button
                    data-testid="bulk-time-anytime"
                    onClick={() => setBulkTimeMode("anytime")}
                    className={`px-3 py-2 rounded-md border-2 text-sm font-semibold text-left ${
                      bulkTimeMode === "anytime" ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-slate-200 text-slate-600"
                    }`}
                  >
                    何時でもOK
                  </button>
                  <button
                    data-testid="bulk-time-custom"
                    onClick={() => setBulkTimeMode("custom")}
                    className={`px-3 py-2 rounded-md border-2 text-sm font-semibold text-left ${
                      bulkTimeMode === "custom" ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-slate-200 text-slate-600"
                    }`}
                  >
                    時間を入力する
                  </button>
                  {bulkTimeMode === "custom" && (
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid="bulk-start" type="time" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} />
                      <Input data-testid="bulk-end" type="time" value={bulkEnd} onChange={(e) => setBulkEnd(e.target.value)} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DrawerFooter>
            <Button data-testid="bulk-apply-button" onClick={applyBulk} className="bg-blue-600 hover:bg-blue-700 h-12 text-base">
              一括適用
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>キャンセル</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
